<template>
  <div class="page">
    <!-- Header -->
    <header class="header" :class="{ 'header-shadow': scrolled }">
      <div class="logo">郑州存者信息技术有限公司</div>
      <button class="menu-toggle" @click="menuOpen = !menuOpen">
        <span :class="{ open: menuOpen }"></span>
      </button>
      <nav :class="{ active: menuOpen }">
        <a
          v-for="item in navItems"
          :key="item.id"
          :href="'#' + item.id"
          :class="{ active: activeSection === item.id }"
          @click="onNavClick(item.id)"
        >{{ item.label }}</a>
      </nav>
    </header>

    <!-- Hero Section -->
    <section class="hero">
      <div class="hero-content">
        <h1>存者信息技术</h1>
        <p>新机代卖 / 社区电商 / 进销存管理</p>
        <button @click="scrollToSection('about')">了解更多</button>
      </div>
    </section>

    <!-- About Section -->
    <section id="about" class="section">
      <h2>关于我们</h2>
      <p>
        郑州存者信息技术有限公司位于郑州通讯新天地A1-06，主要从事全品类新机代卖业务，同时为通讯行业商户提供社区电商和进销存管理工具。
      </p>
      <p>目前公司运营「踏浪」小程序、「弧淘」APP以及自研的ERP管理系统，服务范围覆盖新机采购分销、商户库存管理和行业社区交流。</p>
    </section>

    <!-- Products Section -->
    <section id="products" class="section bg">
      <h2>产品与服务</h2>
      <div class="cards">
        <div class="card">
          <h3>踏浪小程序</h3>
          <p>基于微信生态的新机代卖平台，支持在线询价、下单及物流追踪，面向全国手机零售商户。</p>
        </div>
        <div class="card">
          <h3>弧淘APP</h3>
          <p>集社区内容与商品交易于一体，用户可浏览行业资讯、分享使用体验，同时完成选购下单。</p>
        </div>
        <div class="card">
          <h3>存者ERP</h3>
          <p>面向通讯行业商户的进销存管理系统，涵盖采购入库、销售出库、库存盘点及经营数据统计。</p>
        </div>
      </div>
      <div class="chart-wrapper">
        <h3>月度业务数据</h3>
        <div ref="chart" class="chart"></div>
      </div>
    </section>

    <!-- Map Section -->
    <section id="map" class="section">
      <h2>公司地图</h2>
      <p>总部位置（郑州通讯新天地 A1-06）</p>
      <div class="map">
        <iframe
          src="https://uri.amap.com/marker?position=113.6715,34.742&name=郑州通讯新天地A1-06&src=company_web"
          width="100%"
          height="500"
          frameborder="0"
          style="border-radius:10px;"
          allowfullscreen
        ></iframe>
      </div>
      <p class="map-note">可通过地图导航前往公司</p>
    </section>

    <!-- QR Code Section -->
    <section id="qr" class="section bg">
      <h2>扫码体验</h2>
      <div class="qr-codes">
        <div class="qr">
          <img :src="require('@/static/tl.jpg')" alt="踏浪小程序二维码">
          <h3>踏浪小程序</h3>
        </div>
        <div class="qr">
          <img :src="require('@/static/ht.jpg')" alt="弧淘APP二维码">
          <h3>弧淘APP</h3>
        </div>
      </div>
    </section>

    <!-- Contact Section -->
    <section id="contact" class="section">
      <h2>联系我们</h2>
      <div class="contact-detail">
        <p>地址：郑州通讯新天地A1-06市场档口</p>
        <p>邮箱：tianqicun@maoyishiji.com</p>
        <p>工作时间：周一至周五 9:00 - 18:00</p>
      </div>
      <form class="contact-form" @submit.prevent="submitForm">
        <input type="text" placeholder="您的姓名" v-model="form.name" required />
        <input type="email" placeholder="邮箱地址" v-model="form.email" required />
        <textarea placeholder="留言内容" v-model="form.message" rows="5" required></textarea>
        <button type="submit" :disabled="submitting">
          {{ submitting ? '提交中...' : '提交留言' }}
        </button>
      </form>
    </section>

    <!-- Footer -->
    <footer class="footer">
      <div class="footer-inner">
        <p>© {{ year }} 郑州存者信息技术有限公司 | 备案号: 豫ICP备2021019532号-5</p>
        <p class="footer-links">
          <a href="#about">关于我们</a>
          <a href="#products">产品服务</a>
          <a href="#contact">联系我们</a>
        </p>
      </div>
    </footer>

    <!-- 回到顶部 -->
    <transition name="fade">
      <button v-show="scrolled" class="back-top" @click="backToTop" title="回到顶部">
        &#8679;
      </button>
    </transition>
  </div>
</template>

<script>
import * as echarts from 'echarts';

export default {
  data() {
    return {
      year: new Date().getFullYear(),
      form: {
        name: '',
        email: '',
        message: ''
      },
      submitting: false,
      scrolled: false,
      activeSection: '',
      menuOpen: false,
      navItems: [
        { id: 'about', label: '关于我们' },
        { id: 'products', label: '产品服务' },
        { id: 'map', label: '公司地址' },
        { id: 'qr', label: '扫码体验' },
        { id: 'contact', label: '联系我们' }
      ],
      chartInstance: null
    };
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll);
    this.onScroll();
    this.$nextTick(() => {
      this.initChart();
    });
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.handleResize);
    if (this.chartInstance) {
      this.chartInstance.dispose();
    }
  },
  methods: {
    scrollToSection(id) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    },
    onNavClick(id) {
      this.menuOpen = false;
      this.scrollToSection(id);
    },
    backToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    onScroll() {
      this.scrolled = window.scrollY > 80;
      // 检测当前可见的 section
      var sections = this.navItems.map(function (item) { return item.id; });
      for (var i = sections.length - 1; i >= 0; i--) {
        var el = document.getElementById(sections[i]);
        if (el && el.getBoundingClientRect().top <= 150) {
          this.activeSection = sections[i];
          return;
        }
      }
      this.activeSection = '';
    },
    submitForm() {
      this.submitting = true;
      var self = this;
      // 模拟提交
      setTimeout(function () {
        alert('感谢您的留言，' + self.form.name + '！我们会尽快联系您。');
        self.form.name = '';
        self.form.email = '';
        self.form.message = '';
        self.submitting = false;
      }, 800);
    },
    initChart() {
      var chartDom = this.$refs.chart;
      if (!chartDom) return;
      this.chartInstance = echarts.init(chartDom);
      var option = {
        tooltip: {
          trigger: 'axis'
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: ['1月', '2月', '3月', '4月', '5月', '6月']
        },
        yAxis: {
          type: 'value',
          name: '订单量'
        },
        series: [
          {
            name: '踏浪小程序',
            type: 'bar',
            data: [1820, 1560, 2130, 1940, 2470, 2680],
            itemStyle: { color: '#1677ff' }
          },
          {
            name: '弧淘APP',
            type: 'bar',
            data: [1050, 1230, 1480, 1370, 1760, 1920],
            itemStyle: { color: '#36cfc9' }
          }
        ]
      };
      this.chartInstance.setOption(option);
    },
    handleResize() {
      if (this.chartInstance) {
        this.chartInstance.resize();
      }
    }
  }
};
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.page {
  font-family: 'Microsoft YaHei', Arial, sans-serif;
  color: #333;
}

/* Header */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 50px;
  background: #fff;
  border-bottom: 1px solid #e6e6e6;
  position: sticky;
  top: 0;
  z-index: 1000;
  transition: box-shadow 0.3s;
}
.header-shadow {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}
.logo { font-weight: bold; font-size: 20px; color: #1677ff; white-space: nowrap; }
.header nav a {
  margin-left: 25px;
  color: #333;
  text-decoration: none;
  padding-bottom: 4px;
  border-bottom: 2px solid transparent;
  transition: color 0.2s, border-color 0.2s;
}
.header nav a:hover,
.header nav a.active {
  color: #1677ff;
  border-bottom-color: #1677ff;
}

/* Mobile menu toggle */
.menu-toggle {
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  width: 30px;
  height: 24px;
  position: relative;
}
.menu-toggle span,
.menu-toggle span::before,
.menu-toggle span::after {
  display: block;
  width: 100%;
  height: 3px;
  background: #333;
  border-radius: 2px;
  position: absolute;
  transition: transform 0.3s;
}
.menu-toggle span { top: 50%; transform: translateY(-50%); }
.menu-toggle span::before { content: ''; top: -8px; }
.menu-toggle span::after { content: ''; top: 8px; }
.menu-toggle span.open { background: transparent; }
.menu-toggle span.open::before { top: 0; transform: rotate(45deg); }
.menu-toggle span.open::after { top: 0; transform: rotate(-45deg); }

/* Hero */
.hero {
  background: linear-gradient(to bottom, #e8f1ff, #ffffff);
  padding: 120px 20px;
  text-align: center;
}
.hero h1 { color: #1677ff; font-size: 42px; margin-bottom: 15px; }
.hero p { font-size: 20px; margin-bottom: 30px; color: #555; }
.hero button {
  padding: 14px 30px;
  background: #1677ff;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.3s, transform 0.2s;
}
.hero button:hover { background: #0f5ecf; transform: translateY(-2px); }

/* Sections */
.section { max-width: 1200px; margin: auto; padding: 60px 20px; }
.bg { background: #f7faff; }
.section h2 { font-size: 30px; color: #1677ff; margin-bottom: 20px; text-align: center; }

/* Cards */
.cards { display: flex; gap: 20px; flex-wrap: wrap; margin-top: 30px; }
.card {
  flex: 1;
  min-width: 280px;
  background: #fff;
  padding: 25px;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  transition: transform 0.3s, box-shadow 0.3s;
}
.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 24px rgba(0,0,0,0.1);
}
.card h3 { margin-bottom: 10px; color: #1677ff; }

/* Chart */
.chart-wrapper {
  margin-top: 50px;
  text-align: center;
}
.chart-wrapper h3 {
  color: #1677ff;
  margin-bottom: 15px;
  font-size: 20px;
}
.chart {
  width: 100%;
  max-width: 700px;
  height: 360px;
  margin: 0 auto;
}

/* Map */
.map iframe { width: 100%; height: 500px; border-radius: 10px; border: none; margin-top: 15px; }
.map-note { text-align: center; font-size: 14px; color: #999; margin-top: 8px; }

/* QR Codes */
.qr-codes { display: flex; justify-content: center; gap: 60px; flex-wrap: wrap; margin-top: 25px; }
.qr { text-align: center; }
.qr img {
  width: 160px;
  height: 160px;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
}
.qr h3 { color: #333; margin-top: 10px; font-size: 14px; }

/* Contact */
.contact-detail {
  text-align: center;
  margin-bottom: 20px;
  line-height: 2;
  color: #555;
}
.contact-form { max-width: 600px; margin: 20px auto 0; display: flex; flex-direction: column; gap: 15px; }
.contact-form input, .contact-form textarea {
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #d9d9d9;
  width: 100%;
  font-size: 14px;
  transition: border-color 0.3s;
}
.contact-form input:focus, .contact-form textarea:focus {
  outline: none;
  border-color: #1677ff;
  box-shadow: 0 0 0 2px rgba(22,119,255,0.1);
}
.contact-form button {
  padding: 12px;
  background: #1677ff;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  transition: background 0.3s;
}
.contact-form button:hover { background: #0f5ecf; }
.contact-form button:disabled { background: #a0c4ff; cursor: not-allowed; }

/* Footer */
.footer { text-align: center; padding: 30px 20px; color: #999; background: #f0f2f5; margin-top: 50px; }
.footer-inner p { margin: 4px 0; }
.footer-links { margin-top: 8px; }
.footer-links a { color: #999; text-decoration: none; margin: 0 12px; }
.footer-links a:hover { color: #1677ff; }

/* Back to top */
.back-top {
  position: fixed;
  bottom: 40px;
  right: 30px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #1677ff;
  color: #fff;
  border: none;
  font-size: 22px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(22,119,255,0.3);
  transition: background 0.3s;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
}
.back-top:hover { background: #0f5ecf; }

/* Transitions */
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
.fade-enter, .fade-leave-to { opacity: 0; }

/* Responsive */
@media (max-width: 768px) {
  .header { padding: 12px 20px; flex-wrap: wrap; }
  .menu-toggle { display: block; }
  .header nav {
    display: none;
    width: 100%;
    flex-direction: column;
    margin-top: 10px;
  }
  .header nav.active { display: flex; }
  .header nav a { margin-left: 0; padding: 8px 0; }
  .hero { padding: 80px 20px; }
  .hero h1 { font-size: 28px; }
  .hero p { font-size: 16px; }
  .cards { flex-direction: column; }
  .qr-codes { flex-direction: column; gap: 25px; }
  .chart { height: 280px; }
}
</style>